import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'react-feather';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { VpButton, VpCheckbox, VpIcon, VpLink } from '@vp-ui';
import { useMediaTableContext } from '@src/hooks';
import { showSendMessageDialog } from '@src/redux/modules/app';
import { getContactsEnabled } from '@src/redux/modules/auth';
import { formatDateMmDdYyyy } from '@src/utils';
import {
  StyledChevronIcon,
  StyledIcon,
  StyledTableCell,
  StyledTableRow,
} from '@vp-ui/widgets/VpMediaTable/styles';
import VpMediaIconList from '@vp-ui/widgets/VpMediaTable/VpMediaIconList';
import VpMediaSubTable from '@vp-ui/widgets/VpMediaTable/VpMediaSubTable';
import VpMediaTagsList from '@vp-ui/widgets/VpMediaTable/VpMediaTagsList';
import VpSubmissionInfoMenu from '@vp-ui/widgets/VpMediaTable/VpSubmissionInfoMenu';
import { getIsSelectingSsembleMedia } from '@src/redux/modules/ssemble';
import RewardButton from '@src/components/MediaList/RewardButton';

export const VpMediaTableBodyRow = ({
  submission = {},
  _showSendMessageDialog,
  isContactEnabled,
  isSelectingSsembleMedia,
  history,
}) => {
  const {
    onSubmissionCheck,
    getIsSubmissionPartialSelected,
    getIsSubmissionAllSelected,
    setSelectedSubmissionId,
    setSelectedMediaId,
    pendingExpandVersions,
    setPendingExpandVersions,
  } = useMediaTableContext();
  const submissionId = submission?.campaignSubmissionId;
  const [isOpen, setIsOpen] = useState(false);
  const rowRef = useRef(null);

  const formattedDate = useMemo(
    () => formatDateMmDdYyyy(submission?.submissionDate),
    [submission?.submissionDate]
  );

  let hasMedia = !!(submission?.videoContent?.length || submission?.attachmentsContent?.length);

  if (isSelectingSsembleMedia) {
    hasMedia = !!submission?.videoContent?.length;
  }

  const consolidateTags = (submission) => {
    const allTags = [];
    const autoTags = submission?.autoTags ?? [];
    const videos = submission?.videoContent ?? [];
    const attachments = submission?.attachmentsContent ?? [];

    autoTags.forEach((tag) => allTags.push(tag));
    videos.forEach((video) => (video?.tags ?? []).forEach((tag) => allTags.push(tag)));
    attachments.forEach((attachment) =>
      (attachment?.tags ?? []).forEach((tag) => allTags.push(tag))
    );

    const uniqueTagNames = new Set();
    return allTags.filter((tag) => {
      const tagName = tag?.name ?? tag;
      if (!tagName || uniqueTagNames.has(tagName)) {
        return false;
      }
      uniqueTagNames.add(tagName);
      return true;
    });
  };

  const consolidatedTags = useMemo(() => consolidateTags(submission), [submission]);

  const onRowClick = () => {
    setSelectedSubmissionId(submissionId);
    //Sets media to null if clicking parent row
    setSelectedMediaId(null);
    if (submissionId) {
      history.push({
        pathname: `/media/${submissionId}`,
        search: history.location.search,
      });
    }
  };
  const onParentChevClick = (e) => {
    e.stopPropagation();
    setIsOpen((prevState) => !prevState);
  };
  const onMediaRowClick = (mediaId) => {
    setSelectedSubmissionId(submissionId);
    setSelectedMediaId(mediaId);
    if (submissionId) {
      history.push({
        pathname: `/media/${submissionId}`,
        search: history.location.search,
      });
    }
  };
  const onCheckboxClick = (e) => {
    e.stopPropagation();
    onSubmissionCheck(submissionId);
  };
  const onSendMessageClick = (e) => {
    e.stopPropagation();
    _showSendMessageDialog();
  };

  const onOpenCaptionsClick = (e) => {
    e.stopPropagation();
    setSelectedSubmissionId(submissionId);
    setSelectedMediaId(null);
    if (submissionId) {
      history.push({
        pathname: `/media/${submissionId}`,
        search: history.location.search,
        state: { pendingTab: 1 },
      });
    }
  };

  useEffect(() => {
    setIsOpen(false);
    setSelectedMediaId(null);
  }, [submissionId]);

  useEffect(() => {
    if (!pendingExpandVersions) return;
    const videoIds = (submission?.videoContent || []).map((v) => v.mediaId);
    if (videoIds.includes(pendingExpandVersions)) {
      setIsOpen(true);
      setPendingExpandVersions(null);
      setTimeout(() => {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [pendingExpandVersions]);

  if (!submission?.enabled) {
    const mediaCount = submission?.videoContent?.length + submission?.attachmentsContent?.length;
    const chipCountLabel = mediaCount ? `${mediaCount} media collected` : 'No media collected';
    const expireMessage = 'Submission Locked!';

    return (
      <StyledTableRow key={submissionId} isExpired padding="checkbox">
        <TableCell></TableCell>
        <TableCell padding="checkbox">
          <StyledIcon icon="Lock" />
        </TableCell>
        <TableCell colSpan={3}>
          <strong>{expireMessage}</strong> If you’d like access to this, upgrade now on your{' '}
          <VpLink underline="hover" to="/billing">
            billing page
          </VpLink>
          .
        </TableCell>
        <TableCell>
          <Chip variant="colorTag" label={chipCountLabel} />
        </TableCell>
        <TableCell>{formattedDate}</TableCell>
        <TableCell></TableCell>
      </StyledTableRow>
    );
  }

  return (
    <>
      <StyledTableRow ref={rowRef} hover key={submissionId} onClick={onRowClick}>
        <TableCell padding="checkbox">
          {hasMedia && (
            <StyledChevronIcon fontSize="large" onClick={onParentChevClick}>
              <VpIcon>{isOpen ? <ChevronDown /> : <ChevronRight />}</VpIcon>
            </StyledChevronIcon>
          )}
        </TableCell>
        <TableCell padding="checkbox">
          {hasMedia && !isSelectingSsembleMedia && (
            <VpCheckbox
              indeterminate={getIsSubmissionPartialSelected(submissionId)}
              checked={getIsSubmissionAllSelected(submissionId)}
              onClick={onCheckboxClick}
            />
          )}
        </TableCell>
        <TableCell>{submission?.campaignName}</TableCell>
        <TableCell>{submission?.submitterName}</TableCell>
        <TableCell>
          <VpMediaTagsList tags={consolidatedTags} />
        </TableCell>
        <TableCell>
          {hasMedia ? (
            <VpMediaIconList
              attachments={submission?.attachmentsContent}
              videos={submission?.videoContent}
            />
          ) : (
            <Chip label="No media collected" />
          )}
        </TableCell>
        <TableCell>{formattedDate}</TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center" gap={1}>
            <Stack direction="row" sx={{ minWidth: '40px', justifyContent: 'center' }}>
              {(submission?.campaignRewardEnabled || submission?.giftCardSent) && (
                <RewardButton
                  submission={submission}
                  giftCardSent={!!submission?.giftCardSent}
                  rewardEnabled={!!submission?.campaignRewardEnabled}
                />
              )}
            </Stack>
            {submission?.hasCaptions && (
              <Chip
                label="Captions"
                color="success"
                size="small"
                onClick={onOpenCaptionsClick}
                sx={{ cursor: 'pointer' }}
              />
            )}
            {isContactEnabled && (
              <VpButton
                variant="outlined"
                size="small"
                endIcon={<VpIcon size={24} icon="ReplyIcon" />}
                onClick={onSendMessageClick}
              >
                Reply
              </VpButton>
            )}
            <VpSubmissionInfoMenu submission={submission} canDownload={hasMedia} />
          </Stack>
        </TableCell>
      </StyledTableRow>
      {hasMedia && (
        <TableRow>
          <StyledTableCell colSpan={8}>
            <Collapse unmountOnExit in={isOpen}>
              <VpMediaSubTable
                onMediaRowClick={onMediaRowClick}
                videos={submission?.videoContent}
                attachments={submission?.attachmentsContent}
              />
            </Collapse>
          </StyledTableCell>
        </TableRow>
      )}
    </>
  );
};

VpMediaTableBodyRow.propTypes = {
  _showSendMessageDialog: PropTypes.func,
  isContactEnabled: PropTypes.number,
  submission: PropTypes.object,
  submissionExpireDays: PropTypes.number,
  isSelectingSsembleMedia: PropTypes.bool,
  history: PropTypes.object,
};

const mapStateToProps = (state) => ({
  isContactEnabled: getContactsEnabled(state),
  isSelectingSsembleMedia: getIsSelectingSsembleMedia(state),
});

const mapDispatchToProps = {
  _showSendMessageDialog: showSendMessageDialog,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VpMediaTableBodyRow));



// WEBPACK FOOTER //
// ./src/vp-ui/widgets/VpMediaTable/VpMediaTableBodyRow.jsx